import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Url, URLS } from '@wix/da-url';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { BiData, TopNavMenuClickBiEvent } from '@wix/da-bi/pkg/events';
import MenuList from '@wix/da-ds/pkg/Dropdown/MenuList';
import UserLink from '../../../../../../User/Link';
import VerificationReminder from '../../../../../_partials/VerificationReminder';
import { useCurrentUser } from '../../../../../../utils/hooks/useCurrentUser';
import {
  useBuyCoreItem,
  useLogoutItem,
  useThemeToggleItem,
  useContentFilterItem,
  useAISuppressionItem,
  useHelpItem,
  useSettingsItem,
  useStudioItem,
  usePointsBalanceItem,
  useCustomerDashboardItem,
  useEarningsItem,
  useBillingItem,
  useSaleBannerItem,
  usePushSubscriptionItem,
} from '../../../items';
import { UserMenuContext } from '../../UserMenuContext';
import UserMenuAvatar from '../UserMenuAvatar';

import userMenuStyles from './../../UserMenuMobile.scss';
import s from './LoggedInMenu.scss';

export interface Props {
  hideNotificationCount?: boolean;
  disableThemeSwitcher?: boolean;
  hasSubscriptions?: boolean;
}

const LoggedInMenu: React.FC<Props> = ({
  disableThemeSwitcher,
}) => {
  const { t } = useTranslation();
  const { isOpen: isUserMenuOpen } = useContext(UserMenuContext);
  const currentUser = useCurrentUser();

  const aiSuppressionItem = useAISuppressionItem();
  const contentFilterItem = useContentFilterItem();
  const themeToggleItem = useThemeToggleItem();
  const saleBannerItem = useSaleBannerItem();
  const logoutItem = useLogoutItem();
  const buyCoreItem = useBuyCoreItem();
  const helpItem = useHelpItem();
  const settingsItem = useSettingsItem();
  const studioItem = useStudioItem();
  const pointsBalanceItem = usePointsBalanceItem();
  const customerDashboardItem = useCustomerDashboardItem();
  const earningsItem = useEarningsItem();
  const billingItem = useBillingItem();
  const pushSubscriptionItem = usePushSubscriptionItem();

  const { verificationEmail } = useContext(SessionContext);
  const tabIndex = isUserMenuOpen ? 0 : -1;

  const userAvatarItem = {
    className: userMenuStyles['user-link-root'],
    label: (
      <UserLink
        user={currentUser}
        className={s['user-link']}
        showSymbol={false}
        tabIndex={tabIndex}
      />
    ),
    suffix: <UserMenuAvatar />,
  };

  return (
    <MenuList
      renderOnSSR
      id="site-header-user-menu"
      className={userMenuStyles['user-menu']}
      itemsFillContainerWidth={true}
      items={[
        {
          className: userMenuStyles['top-group'],
          items: [userAvatarItem, buyCoreItem, saleBannerItem],
        },
        {
          className: userMenuStyles['group'],
          items: [
            !!verificationEmail && {
              label: <VerificationReminder email={verificationEmail} />,
              className: s['verification-group'],
            },
            !verificationEmail && studioItem,
            !verificationEmail && {
              label: t('siteHeader.userMenu.myStash'),
              link: Url.buildFromPath(URLS.STASH),
              biData: BiData<TopNavMenuClickBiEvent>({
                evid: 702,
                link_url: Url.buildFromPath(URLS.STASH),
                menu_item: 'stash',
                menu_name: 'profile',
              }),
              tabIndex,
            },
            !verificationEmail && {
              label: t('siteHeader.userMenu.dreamup'),
              link: Url.dreamupLink(),
              biData: BiData<TopNavMenuClickBiEvent>({
                evid: 702,
                link_url: Url.dreamupLink(),
                menu_item: 'dreamup',
                menu_name: 'profile',
              }),
              tabIndex,
            },
            !verificationEmail && pointsBalanceItem,
            !verificationEmail && earningsItem,
            billingItem,
            !verificationEmail && customerDashboardItem,
          ].filter(Boolean),
        },
        {
          className: userMenuStyles['group'],
          items: [
            !disableThemeSwitcher && themeToggleItem,
            contentFilterItem,
            aiSuppressionItem,
          ].filter(Boolean),
        },
        {
          className: userMenuStyles['group'],
          items: [
            settingsItem,
            pushSubscriptionItem,
            helpItem,
            logoutItem,
          ].filter(Boolean),
        },
      ]}
    />
  );
};
LoggedInMenu.displayName = 'LoggedInMenu';

export default LoggedInMenu;
